import { getCookie } from '../../common/utils/cookies';
import { Task, TaskItem } from '../interfaces';
import { TaskItemState } from '../../store/interfaces';
import { TaskItemStatus } from '../../taskList/constants';
import { WorkflowItemCode } from '../../constants';
import { ResponseReuseType } from '../constants/constants';

/**
 * @param tasks - Tasks Array
 * @param itemStatusArray - Task item status array
 * - Maps task items statuses to items in the Task list configuration based on the itemId
 * - Calculate task status on the basis of items status
 */
const taskItemStatusMapper = (tasks: Task[], itemStatusArray: TaskItemState[] | undefined): Task[] => {
  let itemStatusMap: Record<string, TaskItemState> = {};
  let tasksWithItemStatus = tasks;

  if (itemStatusArray && itemStatusArray.length) {
    itemStatusMap = itemStatusArray.reduce((acc: Record<string, TaskItemState>, cur: TaskItemState) => {
      acc[cur.itemId] = cur;
      return acc;
    }, {});

    tasksWithItemStatus = tasks.map((task) => ({
      ...task,
      items: task.items.map((item) => {
        if (itemStatusMap[item.id]) {
          return { ...itemStatusMap[item.id], ...item };
        }
        return item;
      }),
    }));
  }

  // Set state and status for Assessment item for TC Plus flow
  const tcPlusFlow = getCookie('TcPlusFlag') === '1';
  if (tcPlusFlow) {
    tasksWithItemStatus = tasksWithItemStatus.map((task) => ({
      ...task,
      items: task.items.map((item) => {
        if (item.code === WorkflowItemCode.EXPASSES && !item.status && !item.state) {
          return {
            ...item,
            status: TaskItemStatus.NOT_STARTED,
            state: JSON.stringify({ itemId: item.id, taskId: task.id }),
          };
        } else if (item.code === WorkflowItemCode.EXPASSES && item.status && item.state) {
          return {
            ...item,
            state: JSON.stringify({ ...JSON.parse(item?.state), itemId: item.id, taskId: task.id }),
          };
        }
        return item;
      }),
    }));
  }

  const tasksWithStatus = tasksWithItemStatus.map((task) => {
    let responseReuseItem: TaskItem | undefined;
    try {
      responseReuseItem = task.items.find(
        (i) => i.code === 'EXPASSES' && i.state && JSON.parse(i.state).responseReuseType,
      );
      const filterItem = task.items.filter(
        (i) =>
          i.code === 'EXPASSES' &&
          i.state &&
          (JSON.parse(i.state).responseReuseType === ResponseReuseType.FULL_RR ||
            JSON.parse(i.state).responseReuseType === ResponseReuseType.PARTIAL_RR),
      );

      //Handle RR for at task level for multiple assesments
      if (filterItem?.length) {
        const prrItem = filterItem.find(
          (i) => i.state && JSON.parse(i.state).responseReuseType === ResponseReuseType.PARTIAL_RR,
        );
        if (prrItem?.code) responseReuseItem = prrItem;
        else responseReuseItem = filterItem[0];
      }
      if (responseReuseItem)
        task.responseReuseType = responseReuseItem.state && JSON.parse(responseReuseItem.state).responseReuseType;
    } catch (e) {
      console.error('RR state parsing error :', e);
    }

    task.hurdled = task.items.some((i) => i.hurdled);
    task.containAssessmentItem = task.items.some((item) => item.code === WorkflowItemCode.EXPASSES);

    const isTaskHurdleFailed = task.items.some((i) => i.status === TaskItemStatus.HURDLE_FAILED);
    if (isTaskHurdleFailed) {
      task.status = TaskItemStatus.HURDLE_FAILED;
      return task;
    }

    const isTaskCompleted = task.items.every(
      (i) =>
        i.status === TaskItemStatus.COMPLETED ||
        i.status === TaskItemStatus.HURDLE_PASSED ||
        (responseReuseItem &&
          (responseReuseItem.status === TaskItemStatus.COMPLETED ||
            responseReuseItem.status === TaskItemStatus.HURDLE_PASSED)),
    );
    if (isTaskCompleted) {
      task.status = TaskItemStatus.COMPLETED;
      return task;
    }

    const isTaskEvaluationPending = task.items.some((i) => i.status === TaskItemStatus.EVALUATION_PENDING);
    if (isTaskEvaluationPending) {
      task.status = TaskItemStatus.EVALUATION_PENDING;
      return task;
    }

    // After checking above conditions and if none of the items are started, then task is in progress
    const isTaskInProgress = !task.items.every((i) => !i.status || i.status === TaskItemStatus.NOT_STARTED);
    if (isTaskInProgress) {
      task.status = TaskItemStatus.IN_PROGRESS;
      return task;
    }
    return task;
  });

  const unhurdledTasks = [];
  for (let i = 0; i < tasksWithStatus.length; i++) {
    const task = tasksWithStatus[i];
    if (task.hurdled && task.status !== TaskItemStatus.COMPLETED) {
      unhurdledTasks.push(task);
      if (!task.showTasksAfterHurdle) break;
    } else {
      unhurdledTasks.push(task);
    }
  }

  return unhurdledTasks;
};

export default taskItemStatusMapper;
